import type {
  User,
  RoleName,
  ImpersonationState,
  ImpersonatedContext,
  ImpersonationSession,
  UserContext,
} from '../types/auth.types';

export const INIT_IMP: ImpersonationState = {
  isImpersonating: false,
  currentSession: null,
  originalUser: null,
  impersonatedContext: null,
};

export const COMPANY_ROLES: RoleName[] = ['ADMIN', 'MANAGER', 'AGENT'];

export function checkIsCompanyScoped(
  user: User | null,
  impersonation: ImpersonationState,
  hasRole: (role: RoleName) => boolean
): boolean {
  if (!user || impersonation.isImpersonating) return false;
  return (hasRole('AGENT') || hasRole('MANAGER') || hasRole('ADMIN')) && !!user.companyId;
}

export function buildImpersonationState(
  user: User | null,
  session: ImpersonationSession,
  context: ImpersonatedContext
): ImpersonationState | null {
  if (!user) return null;
  const orig: UserContext = {
    userId: user.id,
    email: user.email,
    roles: user.roles,
    companyId: user.companyId,
    companyName: user.companyName,
  };
  return {
    isImpersonating: true,
    currentSession: session,
    originalUser: orig,
    impersonatedContext: context,
  };
}

export function resolveEffectiveCompanyId(
  impersonation: ImpersonationState,
  user: User | null
): string | undefined {
  return impersonation.isImpersonating
    ? impersonation.impersonatedContext?.companyId
    : user?.companyId;
}

export function resolveEffectiveRoles(
  impersonation: ImpersonationState,
  user: User | null
): RoleName[] {
  return impersonation.isImpersonating
    ? (impersonation.impersonatedContext?.effectiveRoles ?? [])
    : (user?.roles ?? []);
}

export function resolveActiveRole(
  activeRole: RoleName | null,
  user: User | null,
  impersonation: ImpersonationState
): RoleName | null {
  if (impersonation.isImpersonating)
    return activeRole && COMPANY_ROLES.includes(activeRole) ? activeRole : 'ADMIN';
  return activeRole ?? user?.roles[0] ?? null;
}

export function resolveAvailableRoles(
  user: User | null,
  impersonation: ImpersonationState
): RoleName[] {
  if (impersonation.isImpersonating) return COMPANY_ROLES;
  return user?.roles.filter((r) => COMPANY_ROLES.includes(r)) ?? [];
}

export function isAllowedRole(
  role: RoleName,
  user: User | null,
  impersonation: ImpersonationState
): boolean {
  const allowedRoles = impersonation.isImpersonating ? COMPANY_ROLES : (user?.roles ?? []);
  return allowedRoles.includes(role);
}
